import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CalendarCheck, Send } from 'lucide-react'
import { toast } from 'sonner'
import { qk } from '@/api/query-keys'
import { getErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { EmptyState, ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { pipelineApi, usePipelineBoard, usePipelineWrite, type CheckinList, type DealStatus } from './api'
import { dateInput, Dot, PipelineFrame, shortDay, STAGE_LABEL, STATUS_LABEL, STATUS_STYLE } from './shared'

interface Row { status: DealStatus | null; nextStep: string; nextStepAt: string; note: string }

/**
 * T1 / T4 — the Friday check-in: one line per open deal, status and next step. Two minutes, and
 * the Monday report writes itself from it. On Fridays Slack sends the link (when Slack is on).
 */
export function CheckinPage() {
  const can = usePipelineBoard().data?.can
  const [all, setAll] = useState(false)
  const list = useQuery({ queryKey: qk.pipelineCheckin(all), queryFn: () => pipelineApi.checkin(all), staleTime: 30_000 })
  // Edits on top of what each deal already says; a fresh load starts from the deal again.
  const [edits, setEdits] = useState<{ at: number; rows: Record<string, Row> }>({ at: 0, rows: {} })
  const base = (d: CheckinList['deals'][number]): Row => ({ status: d.dealStatus, nextStep: d.nextStep ?? '', nextStepAt: dateInput(d.nextStepAt), note: '' })
  const current = edits.at === list.dataUpdatedAt ? edits.rows : {}
  const rows: Record<string, Row> = Object.fromEntries((list.data?.deals ?? []).map((d) => [d.id, current[d.id] ?? base(d)]))
  const setRows = (f: (x: Record<string, Row>) => Record<string, Row>) => setEdits({ at: list.dataUpdatedAt, rows: f(rows) })
  const submit = usePipelineWrite(
    (items: Parameters<typeof pipelineApi.submitCheckin>[0]) => pipelineApi.submitCheckin(items),
    (r) => `Check-in saved for ${r.saved} deal(s)${r.loggedToProjects ? `, ${r.loggedToProjects} written to their project's log` : ''}`,
  )
  const [testing, setTesting] = useState(false)
  const ready = Object.entries(rows).filter(([, r]) => r.status && r.nextStep.trim())
  const send = () =>
    submit.mutate(ready.map(([dealId, r]) => ({ dealId, status: r.status!, nextStep: r.nextStep.trim(), nextStepAt: r.nextStepAt || null, note: r.note.trim() || null })))
  const test = async () => {
    setTesting(true)
    try {
      const r = await pipelineApi.test('checkin')
      toast.success(r.result === 'sent' ? 'Sent to you in Slack' : `Not sent: ${r.result}`)
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setTesting(false)
    }
  }

  return (
    <PipelineFrame
      title="Weekly check-in"
      description="One line per open deal: how it is going and what happens next. Takes two minutes; it goes on the deal, into its project's change log, and into Monday's report."
      actions={
        <>
          {can?.configure ? (
            <Button variant="outline" size="sm" aria-pressed={all} onClick={() => setAll((v) => !v)}>{all ? 'Showing everyone' : 'Show everyone'}</Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => void test()} disabled={testing}><Send className="size-4" />Send me the Slack prompt</Button>
        </>
      }
    >
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <LoadingState label="Loading your deals" />
      ) : !list.data.deals.length ? (
        <EmptyState icon={<CalendarCheck className="size-5" />} title="No open deals of yours" description="Deals are matched to you by your email in GoHighLevel. If yours are missing, check the People list in Settings." />
      ) : (
        <>
          {list.data.meetings.length ? (
            <div className="mb-4 rounded-xl border border-info bg-info-soft px-4 py-3 text-sm text-info">
              <div className="font-medium">You met these recently — what is next?</div>
              <ul className="mt-1 text-xs">
                {list.data.meetings.map((m, i) => <li key={i}>{m.dealName}: “{m.title}”, {shortDay(m.endedAt)}</li>)}
              </ul>
            </div>
          ) : null}
          <ul className="grid gap-2">
            {list.data.deals.map((d) => {
              const r = rows[d.id] ?? { status: null, nextStep: '', nextStepAt: '', note: '' }
              const set = (patch: Partial<Row>) => setRows((x) => ({ ...x, [d.id]: { ...r, ...patch } }))
              return (
                <li key={d.id} className="grid gap-2 rounded-xl border border-border bg-card p-3 shadow-sm lg:grid-cols-[16rem_auto_1fr_9rem]">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold"><Dot level={d.level} />{d.company.name}</div>
                    <div className="text-xs text-muted-foreground">{STAGE_LABEL[d.stage]} · {d.daysInStage}d{all && d.owner.name ? ` · ${d.owner.name}` : ''}{d.lastCheckinAt ? ` · last check-in ${shortDay(d.lastCheckinAt)}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {(['on_track', 'at_risk', 'blocked'] as const).map((s) => (
                      <button key={s} type="button" aria-pressed={r.status === s} onClick={() => set({ status: s })} className={cn('rounded-full px-2 py-1 text-xs', r.status === s ? `${STATUS_STYLE[s]} font-medium` : 'border border-border text-muted-foreground hover:bg-accent')}>
                        {STATUS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                  <div className="grid gap-1.5">
                    <Input value={r.nextStep} onChange={(e) => set({ nextStep: e.target.value })} placeholder="Next step" maxLength={400} className="h-8" />
                    <Input value={r.note} onChange={(e) => set({ note: e.target.value })} placeholder="Anything the team should know (optional)" maxLength={1000} className="h-8" />
                  </div>
                  <Input type="date" value={r.nextStepAt} onChange={(e) => set({ nextStepAt: e.target.value })} className="h-8" aria-label="Next step by" />
                </li>
              )
            })}
          </ul>
          <div className="sticky bottom-0 mt-4 flex items-center justify-end gap-3 border-t border-border-soft bg-background py-3">
            <span className="text-sm text-muted-foreground">{ready.length} of {list.data.deals.length} ready (status and next step)</span>
            <Button disabled={!ready.length || submit.isPending || !can?.edit} onClick={send}>Save the check-in</Button>
          </div>
        </>
      )}
    </PipelineFrame>
  )
}
